"use client";

import React, {
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
	forwardRef,
} from "react";
import { Autocomplete, CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import { CustomTextField, CustomFormLabel } from "../formsComponents";
import { api } from "../../api/api";
import { IDisposition, IDistinctDisposition, initialDistinctDisposition } from "../../views/rsaList/components/CloseComplaint/interfaces/ICcCloseComplaint";

interface IDistinctDispositionAutoSearch {
	value: string;
	disabled: boolean;
	label: string;
	onSelect: (value: IDistinctDisposition) => void;
}

export const DistinctDispositionAutoSearch: React.FC<IDistinctDispositionAutoSearch> = (props) => {
	const { value, disabled, label, onSelect } = props;
	const [options, setOptions] = useState<readonly IDistinctDisposition[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearchText] = useState<string>("");
	
	

	const fetchSuggestion = async (enteredValue: string) => {
		setLoading(true);
		try {
			let searchQuery = "";
			if (enteredValue.length > 0) {
				searchQuery += `disposition=${enteredValue}&`;
			}
          
			
			const url = `/get-quick-rsa-help-distinct-disposition`;

			const res = await api.get(url);
			const data: IDistinctDisposition[] = res.data.result;
			setOptions(data);
		} catch (error) {
			console.error("Error fetching data:", error);
			setOptions([]);
		} finally {
			setLoading(false);
		}
	};

	const debounceFn = useCallback(debounce(fetchSuggestion, 800), []);

	const getOptionLabel = (option: string | IDistinctDisposition) => {
		if (typeof option === "string") {
			return option;
		}
		if (option.disposition) {
			return `${option.disposition}`;
		}
		return `${option.disposition || ""}`;
	};

	useEffect(() => {
		if (search && search !== value) {
			debounceFn(search);
		}
	}, [search, value, debounceFn]);

	useEffect(() => {
		fetchSuggestion("");
	}, []);

    const handleFocus = () => {
        if(value){
            fetchSuggestion(value);
        }
    }

	
	

	

	return (
		<>
			{label && <CustomFormLabel>{label}</CustomFormLabel>}
			<Autocomplete
				id="disposition-auto-search"
				fullWidth
			
				freeSolo
				sx={{
					".MuiOutlinedInput-root": {
						paddingTop: "2px",
						paddingBottom: "2px",
						fontSize: "0.8rem",
						color: "rgb(38, 38, 38)",
						width: "100%",
					},
				}}
				getOptionLabel={getOptionLabel}
				isOptionEqualToValue={(option, value) =>
					typeof option === "string"
						? option === value
						: typeof option !== "string" &&
						  typeof value !== "string" &&
						  option.disposition === value.disposition
				}
				filterOptions={(x) => x}
				options={options}
				autoComplete
                onFocus={handleFocus}
				value={
					options.find((option) => option.disposition === value) ||
					null
				}
				loading={loading}
				disabled={disabled}
				noOptionsText="Type to search"
				onChange={(event, value: string | IDistinctDisposition | null) => {
					if (typeof value === "object" && value !== null) {
						onSelect?.(value);
					} else {
						onSelect?.(initialDistinctDisposition);
					}
				}}
				onInputChange={(event, newInputValue) => {
					setSearchText(newInputValue);
				}}
				renderInput={(params) => (
					<CustomTextField
						{...params}
						fullWidth
						placeholder="Search disposition"
						
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<React.Fragment>
									{loading && (
										<CircularProgress
											color="inherit"
											size={20}
										/>
									)}
									{params.InputProps.endAdornment}
								</React.Fragment>
							),
						}}
					/>
				)}
			/>
		</>
	);
};
